import React, { useContext, useEffect } from 'react';
import FormInput from '../../common/FormInput';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
//@ts-ignore
import ToggleButton from '../../common/toggleButton/ToggleButton';
import { AlloyProcessStages } from './AlloyRepairRequestTypes';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import PanelWithButton from '../../common/panelWithButton/PanelWithButton';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import { PageNames } from '../../../Constants/Common';
import { AlloyRepairRequestContext } from '../../../context/AlloyRepairRequestContext';
import { TranslationHelper } from 'classes/TranslationHelper';
import { useTranslation } from 'react-i18next';

export type AlloyRepairRequestSummaryProps = {
    cancelConfirmText: string;
};

const AlloyRepairRequestSummary = ({
    cancelConfirmText
}: AlloyRepairRequestSummaryProps): JSX.Element => {
    const {
        vehicle,
        alloyRepairRequest,
        onTogglePermission,
        onFinishedAlloyRepairRequest,
        onChangeStage,
        onCancelClick,
        onCancelRepairRequest,
        onCancelUndo,
        onUpdateWheelClick,
        onUpdateAddressClick
    } = useContext(AlloyRepairRequestContext);

    const {
        alloyWheelRepairRequests,
        selectedWheels,
        showCancelAlert,
        repairAddresses,
        selectedRepairAddressId,
        permissionGranted,
        alloyFormErrors: errors
    } = alloyRepairRequest;

    const { t } = useTranslation();
    const quantityOfRepairRequests = alloyWheelRepairRequests.length;
    const selectedRepairAddress = repairAddresses.find(
        (a) => a.id === selectedRepairAddressId
    );

    useEffect(() => {
        GoogleAnalytics.setPageView(PageNames.AlloySummary);
    }, []);

    const errorlist = (): Array<string | undefined> => {
        let pageErrors = [errors?.permissionGranted];
        pageErrors = pageErrors.filter((error) => error !== undefined);
        return pageErrors;
    };

    const onUpdateRepairAddress = () => {
        onChangeStage(AlloyProcessStages.General);
        onUpdateAddressClick(true);
    };

    const onUpdateWheel = (index: number) => {
        onChangeStage(AlloyProcessStages.WheelSelection);
        onUpdateWheelClick(alloyWheelRepairRequests[index].damagedWheel);
    };

    const onBackButtonClick = () => {
        onChangeStage(AlloyProcessStages.WheelSelection);
        GoogleAnalytics.trackEventsGoogleAnalytics(
            'Button click',
            'Alloy wheel button clicks',
            'Clicked back button on summary page'
        );
    };

    const forCancelButton = () => {
        const buttonText =
            selectedWheels.length > 1
                ? t('cancel_repair_requests')
                : t('cancel_repair_request');
        return (
            <button
                aria-label='cancel-button'
                className='btn btn-danger progress-button'
                onClick={onCancelClick}>
                {buttonText}
            </button>
        );
    };
    const cancelButton = forCancelButton();

    return (
        <>
            <div className='card page-content'>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='summary-vehicle-forminput'
                        label={t('vehicle')}>
                        <div
                            id='vehicle-summary-container'
                            className='div-container-style'>
                            <section>
                                <b>{vehicle.vrn}</b>
                                <br />
                                {vehicle.make}
                                <br />
                                {vehicle.model}
                            </section>
                        </div>
                    </FormInput>
                </div>

                <div className='card-body page-content-body'>
                    <FormInput
                        id='summary-repair-address-forminput'
                        label={t('repair_address')}>
                        <PanelWithButton
                            id='address-summary-container'
                            buttonText={t('button_label.update')}
                            onButtonClick={onUpdateRepairAddress}>
                            <address>
                                {selectedRepairAddress.addressLine1}
                                <br />
                                {selectedRepairAddress.addressLine2}
                                <br />
                                {selectedRepairAddress.townOrCity}
                                <br />
                                {selectedRepairAddress.region}
                                <br />
                                {selectedRepairAddress.postcode}
                                <br />
                                {selectedRepairAddress.country}
                            </address>
                        </PanelWithButton>
                    </FormInput>
                </div>

                <div className='card-body page-content-body'>
                    <FormInput
                        id='wheels-summary-forminput'
                        label={t('repair_requests')}>
                        {alloyWheelRepairRequests.map(
                            (repairRequest, index) => (
                                <PanelWithButton
                                    id={`wheel-summary-container-${index.toString()}`}
                                    buttonText={t('button_label.update')}
                                    onButtonClick={() => onUpdateWheel(index)}
                                    key={index}>
                                    <b>{t('wheel_label')}</b>
                                    {TranslationHelper.getTranslatedText(
                                        repairRequest.damagedWheel
                                    )}
                                    <br />
                                    <b>{t('type_of_damage_label')}</b>
                                    {TranslationHelper.getTranslatedText(
                                        repairRequest.damageType
                                    )}
                                    <br />
                                    <b>{t('size_of_damage_label')}</b>
                                    {TranslationHelper.getTranslatedText(
                                        repairRequest.damageSize
                                    )}
                                </PanelWithButton>
                            )
                        )}
                    </FormInput>
                </div>
                <hr />
                <div className='card-body page-content-body'>
                    <FormInput
                        id='terms-form'
                        errorText={errors?.permissionGranted}>
                        <div className='col'>
                            <ToggleButton
                                value={permissionGranted}
                                onChange={onTogglePermission}
                                label={t(
                                    'alloy_repair_request.step_3_toggle_permission_label'
                                )}
                            />
                        </div>
                    </FormInput>
                </div>
                <div className='card-footer page-content-footer'>
                    {errors?.permissionGranted && (
                        <ErrorSummary
                            title={t('summary_error')}
                            errors={errorlist()}
                            className='card-error'
                        />
                    )}
                </div>
            </div>
            <div className='bottom-navigation'>
                <button
                    aria-label='submit-button'
                    style={{ width: '100%', marginBottom: '1rem' }}
                    className='btn btn-primary'
                    onClick={onFinishedAlloyRepairRequest}>
                    {t('alloy_request_submit_button', {
                        quantityOfRepairRequests: quantityOfRepairRequests
                    })}
                </button>
                <button
                    aria-label='back-button'
                    className='btn btn-secondary progress-button'
                    onClick={() => onBackButtonClick()}>
                    {t('back')}
                </button>
                {cancelButton}
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
        </>
    );
};

export default AlloyRepairRequestSummary;
